import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Card, Button, LoadingSpinner, JobCard } from '../components/ui';
import usePendingChanges from '../hooks/usePendingChanges';
import { usePendingChangesContext } from '../contexts/PendingChangesContext';
import VersionHistory from '../components/VersionHistory';

const TableEditor = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useApi();
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('editor');
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const rowsPerPage = 50;

  // Pending changes management
  const {
    pendingChanges,
    isDirty,
    isSaving,
    addChange,
    clearChanges,
    saveChanges,
    hasPendingChange,
    getPendingValue,
  } = usePendingChanges(jobId);

  // Register with global pending changes context for navigation guards
  const { setIsDirty, registerHandlers, unregisterHandlers } = usePendingChangesContext();

  // Fetch all jobs for selector
  const { data: jobsData } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const response = await api.get('/api/jobs');
      return response.data;
    },
  });

  const jobs = jobsData?.jobs || [];
  const completedJobs = jobs.filter((job) => job.status === 'completed');

  // Fetch job data
  const { data: jobData, isLoading, error } = useQuery({
    queryKey: ['jobData', jobId],
    queryFn: async () => {
      const response = await api.get(`/api/data/${jobId}`);
      return response.data;
    },
    enabled: !!jobId,
  });

  // Update cell mutation (currently using pending changes instead)
  // const updateCellMutation = useMutation({
  //   mutationFn: async ({ rowId, field, value }) => {
  //     const response = await api.put(`/api/data/${jobId}/cell`, {
  //       row_id: rowId,
  //       field: field,
  //       value: value,
  //     });
  //     return response.data;
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries(['jobData', jobId]);
  //   },
  // });

  // Add row mutation (currently using pending changes instead)
  // const addRowMutation = useMutation({
  //   mutationFn: async (newRow) => {
  //     const response = await api.post(`/api/data/${jobId}/row`, {
  //       row_data: newRow,
  //       source_file: 'manual_entry'
  //     });
  //     return response.data;
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries(['jobData', jobId]);
  //   },
  // });

  // Delete row mutation (currently using pending changes instead)
  // const deleteRowMutation = useMutation({
  //   mutationFn: async (rowId) => {
  //     const response = await api.delete(`/api/data/${jobId}/row/${rowId}`);
  //     return response.data;
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries(['jobData', jobId]);
  //   },
  // });

  // Navigation guards - warn on unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Sync dirty state with global context for navigation guards
  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  // Register save/clear handlers with context
  useEffect(() => {
    registerHandlers(saveChanges, clearChanges);
    return () => unregisterHandlers();
  }, [registerHandlers, unregisterHandlers, saveChanges, clearChanges]);

  // Tab switch handler with guard
  const handleTabSwitch = (newTab) => {
    if (activeTab === 'editor' && isDirty) {
      const confirmSwitch = window.confirm(
        'You have unsaved changes. Do you want to save before switching tabs?'
      );

      if (confirmSwitch) {
        handleSave().then(() => setActiveTab(newTab));
      } else {
        const discard = window.confirm('Discard changes and switch tabs?');
        if (discard) {
          clearChanges();
          setActiveTab(newTab);
        }
      }
    } else {
      setActiveTab(newTab);
    }
  };

  // Save handler
  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(null);

    try {
      const result = await saveChanges();
      queryClient.invalidateQueries(['jobData', jobId]);
      queryClient.invalidateQueries(['versions', jobId]);
      setSaveSuccess(`Changes saved successfully as version ${result.version_number}`);

      // Clear success message after 5 seconds
      setTimeout(() => setSaveSuccess(null), 5000);
    } catch (error) {
      console.error('Save failed:', error);
      setSaveError('Failed to save changes. Your changes are still pending. Please try again.');

      // Clear error message after 10 seconds
      setTimeout(() => setSaveError(null), 10000);
    }
  };

  // Discard changes handler
  const handleDiscardChanges = () => {
    if (window.confirm('Are you sure you want to discard all unsaved changes?')) {
      clearChanges();
      queryClient.invalidateQueries(['jobData', jobId]);
    }
  };

  // Export CSV
  const exportCSV = () => {
    if (!jobData?.rows) return;

    const headers = jobData.columns.join(',');
    const rows = jobData.rows.map((row) =>
      jobData.columns.map((col) => {
        const value = row[col] || '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jobData.name || 'export'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle cell editing
  const handleCellClick = (rowIndex, column, value) => {
    setEditingCell({ rowIndex, column });
    setEditValue(value || '');
  };

  const handleCellBlur = () => {
    if (editingCell) {
      const row = sortedData[editingCell.rowIndex];
      const currentValue = row[editingCell.column];

      if (editValue !== currentValue) {
        // Add to pending changes instead of immediate mutation
        addChange({
          type: 'update_cell',
          row_id: row.id,
          field: editingCell.column,
          old_value: currentValue,
          value: editValue,
        });
      }
    }
    setEditingCell(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  // Handle sorting with 3 states: null -> asc -> desc -> null
  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.key !== column) {
        // Different column clicked, start with ascending
        return { key: column, direction: 'asc' };
      }

      if (prev.direction === 'asc') {
        // Currently ascending, switch to descending
        return { key: column, direction: 'desc' };
      }

      if (prev.direction === 'desc') {
        // Currently descending, reset to no sort
        return { key: null, direction: 'asc' };
      }

      // Fallback (shouldn't reach here)
      return { key: column, direction: 'asc' };
    });
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!jobData?.rows) return [];

    let sorted = [...jobData.rows];

    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        // Smart type detection for numeric sorting
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);

        // If both values are valid numbers, compare numerically
        if (!isNaN(aNum) && !isNaN(bNum)) {
          const comparison = aNum - bNum;
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        // Otherwise, compare as strings (case-insensitive)
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        const comparison = aStr > bStr ? 1 : -1;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return sorted;
  }, [jobData?.rows, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // Handle add row
  const handleAddRow = () => {
    const newRow = {};
    jobData.columns.forEach((col) => {
      newRow[col] = '';
    });
    // Add to pending changes instead of immediate mutation
    addChange({
      type: 'add_row',
      row_data: newRow,
      source_file: 'manual_entry',
      temp_id: `temp_${Date.now()}`, // Temporary ID for tracking
    });
  };

  // Handle row selection
  const handleRowSelect = (rowId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedData.map(row => row.id)));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedRows.size} row(s)?`)) {
      selectedRows.forEach(rowId => {
        addChange({
          type: 'delete_row',
          row_id: rowId,
        });
      });
      setSelectedRows(new Set());
    }
  };

  // Find all matches in the table (including pending changes)
  const matches = useMemo(() => {
    if (!findText || !jobData?.rows) return [];

    const foundMatches = [];
    jobData.rows.forEach((row) => {
      jobData.columns.forEach((column) => {
        // Get the current value (with pending changes if any)
        const hasChange = hasPendingChange(row.id, column);
        const currentValue = hasChange ? getPendingValue(row.id, column) : row[column];

        const cellValue = String(currentValue || '').toLowerCase();
        const searchText = findText.toLowerCase();

        if (cellValue.includes(searchText)) {
          foundMatches.push({
            rowId: row.id,
            column: column,
            value: currentValue,
          });
        }
      });
    });

    return foundMatches;
  }, [findText, jobData?.rows, jobData?.columns, hasPendingChange, getPendingValue]);

  // Reset to first match when findText changes and scroll to it
  useEffect(() => {
    setCurrentMatchIndex(0);
    if (matches.length > 0) {
      // Scroll to first match
      const match = matches[0];
      const cell = document.querySelector(`[data-row-id="${match.rowId}"][data-column="${match.column}"]`);
      if (cell) {
        cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [findText, matches]);

  // Capture Ctrl+F / Cmd+F to open Find & Replace
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+F (Windows/Linux) or Cmd+F (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowFindReplace(true);
      }
      // ESC to close Find & Replace
      if (e.key === 'Escape' && showFindReplace) {
        setShowFindReplace(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showFindReplace]);

  // Scroll to the current match
  const scrollToMatch = (matchIndex) => {
    if (matches.length === 0 || matchIndex < 0 || matchIndex >= matches.length) return;

    const match = matches[matchIndex];
    // Find the table cell element using data attributes
    const cell = document.querySelector(`[data-row-id="${match.rowId}"][data-column="${match.column}"]`);

    if (cell) {
      cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Handle find and replace navigation
  const handleNextMatch = () => {
    if (matches.length === 0) return;
    const newIndex = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(newIndex);
    scrollToMatch(newIndex);
  };

  const handlePrevMatch = () => {
    if (matches.length === 0) return;
    const newIndex = (currentMatchIndex - 1 + matches.length) % matches.length;
    setCurrentMatchIndex(newIndex);
    scrollToMatch(newIndex);
  };

  // Replace current match
  const handleReplaceCurrent = () => {
    if (matches.length === 0) return;

    const match = matches[currentMatchIndex];
    const oldValue = String(match.value || '');
    const newValue = oldValue.replace(new RegExp(findText, 'gi'), replaceText);

    addChange({
      type: 'update_cell',
      row_id: match.rowId,
      field: match.column,
      old_value: oldValue,
      value: newValue,
    });

    // Move to next match
    handleNextMatch();
  };

  // Replace all matches
  const handleReplaceAll = () => {
    if (matches.length === 0) return;

    if (window.confirm(`Replace all ${matches.length} occurrence(s)?`)) {
      matches.forEach((match) => {
        const oldValue = String(match.value || '');
        const newValue = oldValue.replace(new RegExp(findText, 'gi'), replaceText);

        addChange({
          type: 'update_cell',
          row_id: match.rowId,
          field: match.column,
          old_value: oldValue,
          value: newValue,
        });
      });

      setFindText('');
      setReplaceText('');
      setShowFindReplace(false);
    }
  };

  // Helper function to highlight matches in cell content
  const highlightMatches = (text, rowId, column) => {
    if (!findText || !text) return text;

    const textStr = String(text);
    const searchText = findText.toLowerCase();

    if (!textStr.toLowerCase().includes(searchText)) {
      return textStr;
    }

    // Check if this is the current match
    const isCurrentMatch = matches.length > 0 &&
      matches[currentMatchIndex]?.rowId === rowId &&
      matches[currentMatchIndex]?.column === column;

    const parts = [];
    let lastIndex = 0;
    const regex = new RegExp(findText, 'gi');
    let match;

    while ((match = regex.exec(textStr)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(textStr.substring(lastIndex, match.index));
      }

      // Add highlighted match
      parts.push(
        <mark
          key={match.index}
          className={isCurrentMatch ? 'bg-orange-300 dark:bg-orange-600' : 'bg-yellow-200 dark:bg-yellow-600'}
        >
          {match[0]}
        </mark>
      );

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < textStr.length) {
      parts.push(textStr.substring(lastIndex));
    }

    return parts;
  };

  // No job selected - show job selector
  if (!jobId) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary dark:text-primary-light mb-2">
            Table Editor
          </h1>
          <p className="text-text-secondary-light dark:text-secondary">
            Select a completed job to view and edit data
          </p>
        </div>

        <Card>
          <h2 className="text-xl font-semibold text-text-primary-light dark:text-primary mb-4">
            Select a Job
          </h2>
          {completedJobs.length > 0 ? (
            <div className="space-y-3">
              {completedJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={() => navigate(`/table/${job.id}`)}
                  showActions={false}
                />
              ))}
            </div>
          ) : (
            <p className="text-text-secondary-light dark:text-secondary text-center py-8">
              No completed jobs available. Process some images first.
            </p>
          )}
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-red-600 dark:text-red-400 mb-4">
          Error loading data: {error.message}
        </p>
        <Button onClick={() => navigate('/table')}>Back to Job Selection</Button>
      </div>
    );
  }

  if (!jobData?.rows || jobData.rows.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600 dark:text-gray-400 mb-4">No data available for this job.</p>
        <Button onClick={() => navigate('/table')}>Back to Job Selection</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Job Selector */}
      {completedJobs.length > 1 && (
        <Card padding="sm">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Job:
            </label>
            <select
              value={jobId}
              onChange={(e) => navigate(`/table/${e.target.value}`)}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
            >
              {completedJobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.name} - {new Date(job.created_at).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
        </Card>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-primary dark:text-primary-light">
            {jobData.name || 'Table Editor'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {sortedData.length} rows, {jobData.columns.length} columns
          </p>
        </div>
        <div className="flex gap-3">
          {isDirty && activeTab === 'editor' && (
            <>
              <Button onClick={handleSave} variant="primary" disabled={isSaving}>
                {isSaving ? 'Saving...' : `Save Changes (${pendingChanges.length})`}
              </Button>
              <Button onClick={handleDiscardChanges} variant="secondary">
                Discard
              </Button>
            </>
          )}
          <Button onClick={() => navigate(`/insights/${jobId}`)} variant="secondary">
            View Insights
          </Button>
          <Button onClick={exportCSV} variant="secondary">
            Export CSV
          </Button>
          {activeTab === 'editor' && (
            <>
              <Button onClick={() => setShowFindReplace(!showFindReplace)} variant="secondary">
                {showFindReplace ? 'Hide Find/Replace' : 'Find & Replace'}
              </Button>
              <Button onClick={handleAddRow} variant="primary">
                Add Row
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Pending changes summary */}
      {/* {isDirty && activeTab === 'editor' && (
        <Card variant="warning" padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-amber-900 dark:text-amber-100">Unsaved Changes</p>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {(() => {
                  const summary = getChangeSummary();
                  const parts = [];
                  if (summary.cells_updated > 0) parts.push(`${summary.cells_updated} cells updated`);
                  if (summary.rows_added > 0) parts.push(`${summary.rows_added} rows added`);
                  if (summary.rows_deleted > 0) parts.push(`${summary.rows_deleted} rows deleted`);
                  return parts.join(', ');
                })()}
              </p>
            </div>
          </div>
        </Card>
      )} */}

      {/* Success notification */}
      {saveSuccess && (
        <Card padding="sm" className="mb-4 bg-green-50 dark:bg-green-900/20">
          <p className="text-green-800 dark:text-green-200">{saveSuccess}</p>
        </Card>
      )}

      {/* Error notification */}
      {saveError && (
        <Card padding="sm" className="mb-4 bg-red-50 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-200">{saveError}</p>
        </Card>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'editor', label: 'Editor' },
            { id: 'history', label: 'History' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabSwitch(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary dark:text-primary-dark'
                  : 'border-transparent text-text-secondary-light hover:text-text-primary-light hover:border-border-light dark:text-secondary dark:hover:text-primary'
              }`}
            >
              {tab.label}
              {tab.id === 'editor' && isDirty && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-amber-800 bg-amber-200 rounded-full">
                  {pendingChanges.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'editor' && (
        <>
          {/* Find and Replace Panel - Compact Horizontal Layout */}
          {showFindReplace && (
            <div className="fixed top-10 right-4 z-50 animate-slideInFromTop">
              <Card padding="sm" className="bg-white dark:bg-surface-dark shadow-sm border border-gray-200 dark:border-gray-700 py-3 px-3">
                <div className="flex items-center gap-3">
                  {/* Input section */}
                  <div className="flex flex-col gap-1">
                    <input
                      type="text"
                      value={findText}
                      onChange={(e) => setFindText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && matches.length > 0) {
                          e.preventDefault();
                          handleNextMatch();
                        }
                      }}
                      placeholder="Find..."
                      className="w-40 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none dark:bg-gray-700 dark:text-white"
                    />
                    <input
                      type="text"
                      value={replaceText}
                      onChange={(e) => setReplaceText(e.target.value)}
                      placeholder="Replace..."
                      className="w-40 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Match counter */}
                  <div className="text-xs text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
                    {matches.length > 0 ? (
                      <span className="font-medium">{currentMatchIndex + 1} of {matches.length}</span>
                    ) : (
                      findText ? <span className="text-red-500">No Results</span> : ''
                    )}
                  </div>

                  {/* Navigation arrows */}
                  <div className="flex gap-1">
                    <button
                      onClick={handlePrevMatch}
                      disabled={matches.length === 0}
                      className="p-1 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Previous"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleNextMatch}
                      disabled={matches.length === 0}
                      className="p-1 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Next"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Replace buttons */}
                  <div className="flex gap-1">
                    <button
                      onClick={handleReplaceCurrent}
                      disabled={matches.length === 0}
                      className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Replace current"
                    >
                      Replace
                    </button>
                    <button
                      onClick={handleReplaceAll}
                      disabled={matches.length === 0}
                      className="px-2 py-1 text-xs font-medium text-white bg-brand-primary hover:bg-brand-primary/90 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Replace all"
                    >
                      All
                    </button>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={() => setShowFindReplace(false)}
                    className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Close"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </Card>
            </div>
          )}

          {/* Action Bar - Fixed position to prevent layout shift */}
          {selectedRows.size > 0 && (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 animate-fadeIn">
              <Card padding="sm" className="shadow-sm border-2 border-blue-500 dark:border-blue-400">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    title="Delete selected rows"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                  <button
                    onClick={() => setSelectedRows(new Set())}
                    className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Clear selection"
                  >
                    Clear
                  </button>
                </div>
              </Card>
            </div>
          )}

          <Card>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-2 py-1 w-12">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-primary focus:ring-brand-primary cursor-pointer"
                  />
                </th>
                {jobData.columns.map((column) => (
                  <th
                    key={column}
                    onClick={() => handleSort(column)}
                    className="px-2 py-1 text-left text-xs font-medium text-text-secondary-light dark:text-secondary uppercase tracking-wider cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {column}
                      {sortConfig.key === column && (
                        <span className="text-amber-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedData.map((row, rowIndex) => (
                <tr key={row.id} className={selectedRows.has(row.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}>
                  <td className="px-2 pt-1.5 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(row.id)}
                      onChange={() => handleRowSelect(row.id)}
                      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  </td>
                  {jobData.columns.map((column) => {
                    const hasChange = hasPendingChange(row.id, column);
                    const pendingValue = getPendingValue(row.id, column);
                    const displayValue = hasChange ? pendingValue : row[column];

                    return (
                      <td
                        key={column}
                        data-row-id={row.id}
                        data-column={column}
                        className={`px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 cursor-pointer ${
                          hasChange
                            ? 'bg-amber-50 dark:bg-amber-900/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                        onClick={() => handleCellClick(rowIndex, column, displayValue)}
                      >
                        {editingCell?.rowIndex === rowIndex && editingCell?.column === column ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className="w-full px-1.5 py-0.5 text-sm border border-border rounded focus:outline-none dark:bg-gray-700 dark:text-white"
                          />
                        ) : (
                          <div className="min-h-[20px]">
                            {findText ? highlightMatches(displayValue, row.id, column) : (displayValue || '')}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
        </Card>
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <VersionHistory jobId={jobId} />
      )}
    </div>
  );
};

export default TableEditor;
